import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

/**
 * FURD Design System Components
 * Brand Blue: #2563eb (blue-600)
 * All components use consistent styling derived from the original blue
 */

// ==================== FADE IN ON SCROLL ====================
export const FadeInOnScroll = ({ children, delay = 0, className = '', direction = 'up' }) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setIsVisible(true);
      return;
    }
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );
    
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const getTransform = () => {
    if (isVisible) return 'translate(0, 0)';
    switch (direction) {
      case 'left': return 'translateX(-30px)';
      case 'right': return 'translateX(30px)';
      case 'down': return 'translateY(-20px)';
      default: return 'translateY(20px)';
    }
  };
  
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${className}`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: getTransform(),
        transitionDelay: `${delay}ms`
      }}
    >
      {children}
    </div>
  );
};

// ==================== PAGE HEADER ====================
export const PageHeader = ({ 
  badge, 
  badgeIcon: BadgeIcon,
  title, 
  subtitle,
  className = ''
}) => (
  <section className={`relative py-20 md:py-24 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 overflow-hidden ${className}`}>
    {/* Decorative elements */}
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute -top-[200px] -right-[200px] w-[500px] h-[500px] bg-blue-500/15 rounded-full blur-3xl" />
      <div className="absolute -bottom-[150px] -left-[150px] w-[400px] h-[400px] bg-blue-400/10 rounded-full blur-3xl" />
    </div>
    
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
      {badge && (
        <FadeInOnScroll delay={100}>
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/20 backdrop-blur-sm rounded-full text-blue-300 text-sm font-semibold mb-6 border border-blue-400/20">
            {BadgeIcon && <BadgeIcon className="w-4 h-4" />}
            {badge}
          </span>
        </FadeInOnScroll>
      )}
      <FadeInOnScroll delay={200}>
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 tracking-tight">
          {title}
        </h1>
      </FadeInOnScroll>
      {subtitle && (
        <FadeInOnScroll delay={300}>
          <p className="text-lg md:text-xl text-blue-100/80 max-w-3xl mx-auto leading-relaxed">
            {subtitle}
          </p>
        </FadeInOnScroll>
      )}
    </div>
  </section>
);

// ==================== SECTION HEADER ====================
export const SectionHeader = ({ 
  badge, 
  title, 
  subtitle,
  titleHighlight,
  centered = true,
  className = ''
}) => (
  <FadeInOnScroll className={`mb-12 md:mb-16 ${centered ? 'text-center' : ''} ${className}`}>
    {badge && (
      <span className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600/10 to-blue-500/10 rounded-full text-blue-600 font-semibold text-sm uppercase tracking-widest mb-4 border border-blue-200/50">
        <span className="w-2 h-2 rounded-full bg-blue-600 animate-pulse" />
        {badge}
      </span>
    )}
    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
      {titleHighlight ? (
        <>
          {title}{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-500">
            {titleHighlight}
          </span>
        </>
      ) : title}
    </h2>
    {subtitle && (
      <p className={`text-lg md:text-xl text-gray-500 leading-relaxed ${centered ? 'max-w-2xl mx-auto' : ''}`}>
        {subtitle}
      </p>
    )}
  </FadeInOnScroll>
);

// ==================== STANDARD CARD ====================
export const StandardCard = ({ 
  children, 
  className = '',
  hover = true,
  onClick,
  as: Component = 'div'
}) => (
  <Component
    onClick={onClick}
    className={`
      bg-white rounded-2xl p-6 md:p-8 
      shadow-sm border border-gray-100/80
      ${hover ? 'hover:shadow-xl hover:border-blue-200/50 hover:-translate-y-1 cursor-pointer' : ''}
      transition-all duration-300
      ${className}
    `}
  >
    {children}
  </Component>
);

// ==================== FEATURE CARD ====================
export const FeatureCard = ({ 
  icon: Icon, 
  title, 
  description,
  link,
  linkText = 'Learn more',
  delay = 0,
  className = ''
}) => {
  const CardWrapper = link ? Link : 'div';
  const wrapperProps = link ? { to: link } : {};
  
  return (
    <FadeInOnScroll delay={delay}>
      <CardWrapper 
        {...wrapperProps}
        className={`group block h-full bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100/80 hover:shadow-xl hover:border-blue-200/50 hover:-translate-y-1 transition-all duration-300 ${className}`}
      >
        {Icon && (
          <div className="relative mb-5">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-blue-400 rounded-xl blur-lg opacity-20 group-hover:opacity-40 transition-opacity" />
            <div className="relative w-14 h-14 bg-gradient-to-br from-blue-600 to-blue-500 rounded-xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-2 transition-all duration-300 shadow-lg">
              <Icon className="w-7 h-7 text-white" />
            </div>
          </div>
        )}
        <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
          {title}
        </h3>
        <p className="text-gray-500 leading-relaxed mb-4 line-clamp-3">
          {description}
        </p>
        {link && (
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-blue-600 group-hover:gap-3 transition-all relative">
            {linkText}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-600 group-hover:w-full transition-all duration-300" />
          </span>
        )}
      </CardWrapper>
    </FadeInOnScroll>
  );
};

// ==================== ICON CARD ====================
export const IconCard = ({ 
  icon: Icon, 
  title, 
  subtitle,
  delay = 0,
  className = ''
}) => (
  <FadeInOnScroll delay={delay}>
    <div className={`group bg-gray-50 rounded-2xl p-6 flex flex-col items-center text-center hover:bg-blue-50/50 transition-all duration-300 ${className}`}>
      {Icon && (
        <div className="w-14 h-14 bg-gradient-to-br from-blue-100 to-blue-50 rounded-xl flex items-center justify-center mb-4 group-hover:from-blue-600 group-hover:to-blue-500 transition-all duration-300 shadow-sm">
          <Icon className="w-7 h-7 text-blue-600 group-hover:text-white transition-colors" />
        </div>
      )}
      <h3 className="font-semibold text-gray-900">{title}</h3>
      {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
    </div>
  </FadeInOnScroll>
);

// ==================== PRIMARY BUTTON ====================
export const PrimaryButton = ({ 
  children, 
  to, 
  href,
  onClick,
  icon: Icon,
  size = 'default',
  className = ''
}) => {
  const sizeClasses = {
    sm: 'px-4 py-2 text-sm',
    default: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg'
  };
  
  const buttonClasses = `
    inline-flex items-center justify-center gap-2
    bg-gradient-to-r from-blue-600 to-blue-500 
    hover:from-blue-700 hover:to-blue-600
    text-white font-semibold rounded-xl
    shadow-lg hover:shadow-xl hover:shadow-blue-600/20
    transition-all duration-300 hover:scale-105
    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
    ${sizeClasses[size]}
    ${className}
  `;
  
  if (to) {
    return (
      <Link to={to} className={buttonClasses}>
        {children}
        {Icon && <Icon className="w-5 h-5" />}
      </Link>
    );
  }
  
  if (href) {
    return (
      <a href={href} className={buttonClasses} target="_blank" rel="noopener noreferrer">
        {children}
        {Icon && <Icon className="w-5 h-5" />}
      </a>
    );
  }
  
  return (
    <button onClick={onClick} className={buttonClasses}>
      {children}
      {Icon && <Icon className="w-5 h-5" />}
    </button>
  );
};

// ==================== SECONDARY BUTTON ====================
export const SecondaryButton = ({ 
  children, 
  to, 
  href,
  onClick,
  icon: Icon,
  size = 'default',
  className = ''
}) => {
  const sizeClasses = {
    sm: 'px-4 py-2 text-sm',
    default: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg'
  };
  
  const buttonClasses = `
    inline-flex items-center justify-center gap-2
    bg-white border-2 border-gray-200
    hover:border-blue-300 hover:bg-blue-50
    text-gray-700 hover:text-blue-600 font-semibold rounded-xl
    transition-all duration-300 hover:scale-105
    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
    ${sizeClasses[size]}
    ${className}
  `;
  
  if (to) {
    return (
      <Link to={to} className={buttonClasses}>
        {children}
        {Icon && <Icon className="w-5 h-5" />}
      </Link>
    );
  }
  
  if (href) {
    return (
      <a href={href} className={buttonClasses} target="_blank" rel="noopener noreferrer">
        {children}
        {Icon && <Icon className="w-5 h-5" />}
      </a>
    );
  }
  
  return (
    <button onClick={onClick} className={buttonClasses}>
      {children}
      {Icon && <Icon className="w-5 h-5" />}
    </button>
  );
};

// ==================== BREADCRUMBS ====================
export const Breadcrumbs = ({ items, className = '' }) => (
  <nav className={`flex items-center gap-2 text-sm text-gray-500 ${className}`}>
    <Link to="/" className="hover:text-blue-600 transition-colors">Home</Link>
    {items.map((item, index) => (
      <React.Fragment key={index}>
        <ChevronRight className="w-4 h-4" />
        {item.href ? (
          <Link to={item.href} className="hover:text-blue-600 transition-colors">
            {item.label}
          </Link>
        ) : (
          <span className="text-gray-900 font-medium">{item.label}</span>
        )}
      </React.Fragment>
    ))}
  </nav>
);

// ==================== SECTION WRAPPER ====================
export const Section = ({ 
  children, 
  className = '',
  background = 'white',
  padding = 'default',
  id
}) => {
  const bgClasses = {
    white: 'bg-white',
    light: 'bg-gradient-to-b from-slate-50 to-white',
    gradient: 'bg-gradient-to-b from-white via-blue-50/30 to-white',
    dark: 'bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800'
  };
  
  const paddingClasses = {
    none: '',
    sm: 'py-12 md:py-16',
    default: 'py-16 md:py-24',
    lg: 'py-24 md:py-32'
  };
  
  return (
    <section id={id} className={`${bgClasses[background]} ${paddingClasses[padding]} relative overflow-hidden ${className}`}>
      {children}
    </section>
  );
};

// ==================== CONTAINER ====================
export const Container = ({ children, className = '' }) => (
  <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

// ==================== DIVIDER ====================
export const Divider = ({ className = '' }) => (
  <div className={`w-full h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent ${className}`} />
);

// ==================== STAT CARD ====================
export const StatCard = ({ value, label, delay = 0, className = '' }) => (
  <FadeInOnScroll delay={delay}>
    <div className={`text-center p-6 ${className}`}>
      <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">{value}</div>
      <div className="text-gray-500 font-medium">{label}</div>
    </div>
  </FadeInOnScroll>
);

// ==================== IMAGE WITH FALLBACK ====================
export const ResponsiveImage = ({ 
  src, 
  alt, 
  className = '',
  fallback = null,
  aspectRatio = 'auto'
}) => {
  const [error, setError] = useState(false);
  const [loaded, setLoaded] = useState(false);
  
  const aspectClasses = {
    auto: '',
    square: 'aspect-square',
    video: 'aspect-video',
    portrait: 'aspect-[3/4]'
  };
  
  if (error || !src) {
    return fallback || (
      <div className={`bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center ${aspectClasses[aspectRatio]} ${className}`}>
        <span className="text-gray-400 text-sm">No image</span>
      </div>
    );
  }
  
  return (
    <div className={`relative overflow-hidden ${aspectClasses[aspectRatio]} ${className}`}>
      {!loaded && (
        <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-50 animate-pulse" />
      )}
      <img
        src={src}
        alt={alt}
        className={`w-full h-full object-cover transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
      />
    </div>
  );
};

export default {
  FadeInOnScroll,
  PageHeader,
  SectionHeader,
  StandardCard,
  FeatureCard,
  IconCard,
  PrimaryButton,
  SecondaryButton,
  Breadcrumbs,
  Section,
  Container,
  Divider,
  StatCard,
  ResponsiveImage
};
